import { DateTime } from "luxon"
import { REPORT_PERIOD_BY_LANG } from "../consts/date.consts"
import { UNIFIEDDATE_VALUES } from "../consts/CompoundRangeDatePicker"
import {
  CLIENT_INDIVIDUAL_REPORT_CSV_HEADERS,
  INDIVIDUAL_REPORT_ROW_HEIGHT,
  INDIVIDUAL_REPORT_SUBHEADER_HEIGHT,
} from "../consts/reports.consts"
import { getLastMonthDateRange } from "./date.utils"

/**
 * @typedef {import("./report.utils").FormattedCategory} FormattedCategory
 * @typedef {import("./report.utils").FormattedModule} FormattedModule
 * @typedef {import("./report.utils").ProjectGroup} ProjectGroup
 * @typedef {import("./report.utils").TeamMemberGroup} TeamMemberGroup
 * @typedef {import("./report.utils").FormattedTeamMember} FormattedTeamMember
 * @typedef {import("../services/reports.service.types").AnyTaskReportItem} AnyTaskReportItem
 * @typedef {import("../services/reports.service.types").IndividualTaskReportItem} IndividualTaskReportItem
 * @typedef {import("../services/reports.service.types").GlobalTaskReportItem} GlobalTaskReportItem
 */

export const roundReportData = (obj) => {
  for (const key in obj) {
    if (typeof obj[key] === "number") {
      obj[key] = parseFloat(obj[key].toFixed(2))
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      roundReportData(obj[key])
    }
  }
  return obj
}

export const roundReportData3Decimals = (obj) => {
  for (const key in obj) {
    if (typeof obj[key] === "number") {
      obj[key] = parseFloat(obj[key].toFixed(3))
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      roundReportData3Decimals(obj[key])
    }
  }
  return obj
}

export const getUniqueDatesFromWeeklyReport = (dataset) => {
  if (!dataset || dataset.length === 0) return null
  const uniqueDatesSet = new Set(
    dataset.flatMap((item) => Object.keys(item.week))
  )
  const uniqueDates = Array.from(uniqueDatesSet)
  return uniqueDates
}

/**
 * Calculate end date from a start date and a period type
 * Example: '2024-11-01' + 'weekly' => '2024-11-08'
 *
 * @param {string} period
 * @param {DateTime} startDate
 * @param {'es' | 'eng'} lang
 * @returns {{startDate, endDate}}
 */
export const calculateDateRangeWithPeriodAndStartDate = (
  period,
  startDate,
  lang = "en",
  endDate = null
) => {
  let newStartDate = startDate
  let calculatedEndDate
  const getValidDate = (date) => {
    if (date.isValid) {
      return date
    }
    if (DateTime.fromJSDate(date).isValid) {
      return DateTime.fromJSDate(date)
    }
    if (DateTime.fromISO(date).isValid) {
      return DateTime.fromISO(date)
    }
  }
  switch (period) {
    case REPORT_PERIOD_BY_LANG[lang].DIARIO:
      newStartDate = getValidDate(startDate)
      calculatedEndDate = getValidDate(startDate)
        .set({ hour: 12 })
        .startOf("day")
      break
    case REPORT_PERIOD_BY_LANG[lang].SEMANAL:
      newStartDate = getValidDate(startDate).startOf("week").set({ hour: 12 })
      calculatedEndDate = getValidDate(startDate)
        .endOf("week")
        .set({ hour: 12 })
      break
    case REPORT_PERIOD_BY_LANG[lang].MENSUAL:
      newStartDate = getValidDate(startDate).startOf("month").set({ hour: 12 })
      calculatedEndDate = getValidDate(startDate)
        .endOf("month")
        .set({ hour: 12 })
      break
    case REPORT_PERIOD_BY_LANG[lang].ANUAL:
      newStartDate = getValidDate(startDate).startOf("year").set({ hour: 12 })
      calculatedEndDate = getValidDate(startDate)
        .endOf("year")
        .set({ hour: 12 })
      break
    case REPORT_PERIOD_BY_LANG[lang].CUSTOM:
      newStartDate = getValidDate(startDate)
      calculatedEndDate = getValidDate(endDate)
      break
    default:
      throw new Error(
        "Periodo no soportado, las opciones son: " +
          Object.values(REPORT_PERIOD_BY_LANG[lang]).join(", ")
      )
  }
  return { startDate: newStartDate, endDate: calculatedEndDate }
}

export const getExportFilename = (base, dateRange, period) => {
  let periodStr = ""
  const [startDate, endDate] = dateRange

  switch (period) {
    case UNIFIEDDATE_VALUES.SINGLEDAY:
      {
        const day = String(startDate.day).padStart(2, "0")
        const month = String(startDate.month).padStart(2, "0")
        const year = String(startDate.year).slice(-2)
        periodStr = `${day}-${month}-${year}`
      }
      break
    case UNIFIEDDATE_VALUES.SINGLEWEEK:
      {
        const weekNumber = startDate.weekNumber
        const year = startDate.year.toString().slice(-2)
        periodStr = "Semana" + weekNumber + "-" + year
      }
      break
    case UNIFIEDDATE_VALUES.SINGLEMONTH:
      {
        const month =
          startDate.setLocale("es").monthLong.charAt(0).toUpperCase() +
          startDate.setLocale("es").monthLong.slice(1)
        const year = startDate.year.toString().slice(-2)
        periodStr = month + "-" + year
      }
      break
    case UNIFIEDDATE_VALUES.RANGEDAY:
      {
        const day1 = String(startDate.day).padStart(2, "0")
        const month1 = String(startDate.month).padStart(2, "0")
        const year1 = String(startDate.year).slice(-2)

        const day2 = String(endDate.day).padStart(2, "0")
        const month2 = String(endDate.month).padStart(2, "0")
        const year2 = String(endDate.year).slice(-2)
        periodStr = `${day1}-${month1}-${year1}_${day2}-${month2}-${year2}`
      }
      break
    default:
      periodStr = "Error"
      throw new Error(`Periodo ${period} no válido al intentar exportar`)
  }
  return `${base}_${periodStr}`
}

export const convertToCSV = (data, headers) => {
  const array = Array.isArray(data) ? data : JSON.parse(data)
  const headerRow = headers.map((header) => `"${header.header}"`).join(",")
  const rows = array.map((item) =>
    headers
      .map(
        (header) => `"${String(item[header.field] ?? "").replace(/"/g, '""')}"`
      )
      .join(",")
  )
  return [headerRow, ...rows].join("\r\n")
}

export const downloadCSV = (data, filename) => {
  const blob = new Blob(["\ufeff" + data], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const getContextMenuItemsForIndividualReport = (
  selectedTask,
  cmItems
) => {
  if (!selectedTask) {
    return []
  }
  if (!selectedTask?.hasChangelog) {
    return cmItems.slice(0, 1)
  }
  return cmItems
}

export const calculateTotalsByDayForIndividualTasksReport = (data) => {
  return data.reduce((acc, task) => {
    const day = new Date(task.date).toISOString().split("T")[0]
    if (!acc[day]) {
      acc[day] = {
        sisuTime: 0,
        billedTime: 0,
        timeDifference: 0,
      }
    }
    acc[day].sisuTime += task.sisuTime
    acc[day].billedTime += task.billedTime === "N/A" ? 0 : task.billedTime
    acc[day].timeDifference +=
      task.timeDifference === "N/A" ? 0 : Math.abs(task.timeDifference)
    return acc
  }, {})
}

export const calculateTotalsIndividualTasksReport = (data) => {
  return data.reduce(
    (acc, task) => {
      acc.sisuTime += task.sisuTime
      acc.billedTime += task.billedTime === "N/A" ? 0 : task.billedTime
      acc.timeDifference +=
        task.timeDifference === "N/A" ? 0 : Math.abs(task.timeDifference)
      return acc
    },
    {
      sisuTime: 0,
      billedTime: 0,
      timeDifference: 0,
    }
  )
}

export const calculateLazyScrollIndexOffset = (
  tasks,
  currentIndex,
  sortConfig = {}
) => {
  let numberOfUniqueDays = getLazyUniqueDaysDisplayed(
    tasks,
    currentIndex,
    sortConfig
  )
  numberOfUniqueDays =
    numberOfUniqueDays < 0 && currentIndex === 0 ? 1 : numberOfUniqueDays
  const newOffset =
    currentIndex * INDIVIDUAL_REPORT_ROW_HEIGHT +
    numberOfUniqueDays * INDIVIDUAL_REPORT_SUBHEADER_HEIGHT
  return newOffset
}

export const getLazyUniqueDaysDisplayed = (tasks, currentIndex, sortConfig) => {
  if (!tasks || tasks.length === 0) {
    return 0
  }
  if (sortConfig?.sortField) {
    return tasks
      .slice(0, currentIndex)
      .filter(
        (value, index, array) =>
          index === 0 || value.date !== array[index - 1].date
      ).length
  }
  const uniqueDays = new Set()
  for (let i = 0; i < currentIndex; i++) {
    uniqueDays.add(tasks[i]?.date)
  }
  return uniqueDays.size
}

export const formatCategoriesDropdownFilter = (categories, annotated) => {
  if (!categories) return []
  return [
    ...new Map(
      categories.map((item) => {
        const id = item?.specialCategoryId || item?.categoryId
        const label = item?.specialCategoryTitle || item?.categoryTitle
        const isSpecialCategory = Boolean(item?.specialCategoryId)

        let value = item?.specialCategoryId || item?.categoryId

        if (annotated) {
          if (isSpecialCategory) {
            value = `special:${value}`
          } else {
            value = `normal:${value}`
          }
        }

        return [
          id,
          {
            label,
            value,
            isSpecialCategory,
          },
        ]
      })
    ).values(),
  ].filter((c) => c.label)
}

export const formatModulesDropdownFilter = (modules) => {
  return [
    ...new Map(
      modules?.map((item) => {
        return [item.module, { label: item.module, value: item.moduleId }]
      })
    ).values(),
  ]
}

export const formatProjectsDropdownFilter = (incomingProjects) => {
  const projects = []
  incomingProjects?.forEach((item) => {
    let project = projects.find((project) => project.name === item?.clientName)
    if (!project) {
      project = {
        name: item?.clientName,
        code: item?.clientCode,
        type: "client",
        items: [],
      }
      projects.push(project)
    }
    let projectItem = project.items.find(
      (project) => project.name === item?.projectName
    )
    if (!projectItem) {
      projectItem = {
        id: item?.projectId,
        name: item?.projectName,
        code: item?.projectCode,
        clientCode: item?.clientCode,
        type: "project",
      }
      project.items.push(projectItem)
    }
  })
  return projects
}

export const formatTeamMembersDropdownFilter = (teamMembers) => {
  return [
    ...new Map(
      teamMembers?.map((item) => {
        return [
          item?.teamMemberTagName,
          {
            label: `${item?.teamMemberTagName} - ${item?.teamMemberName} ${item?.teamMemberLastName}`,
            value: item?.teamMemberTagName,
            isActive: item?.teamMemberIsActive,
          },
        ]
      })
    ).values(),
  ]
    .reduce(
      (acc, next) => {
        if (next.isActive) {
          acc[0].items.push(next)
        } else {
          acc[1].items.push(next)
        }
        return acc
      },
      [
        {
          active: true,
          items: [],
        },
        {
          active: false,
          items: [],
        },
      ]
    )
    .filter((el) => el.items.length > 0)
}

export const resolveDateRangeFromShortcutState = (state) => {
  switch (state.dateRange) {
    case "last-month":
      return getLastMonthDateRange()
    case "start-date-to-today": {
      const startDate = DateTime.fromISO(state.dateRangeStart)
      const today = DateTime.now()
      return [startDate, today]
    }
    default:
      return null
  }
}

export const normalizeTasksForCSV = (tasks = []) => {
  return tasks
    .filter((task) => !task.isSubheader)
    .map((task) => ({
      ...task,
      date: DateTime.fromISO(task?.date?.split("T")[0])
        .set({ hour: 12 })
        .toISODate(),
    }))
}

export const hasTasksWithSpecialCategories = (tasks = []) => {
  return tasks.some((task) => Boolean(task.specialCategoryId))
}

export const buildHeadersForCSV = (tasks = []) => {
  const hasSpecialCategories = hasTasksWithSpecialCategories(tasks)

  let headers = CLIENT_INDIVIDUAL_REPORT_CSV_HEADERS

  if (!hasSpecialCategories) {
    headers = headers.filter((item) => item.field !== "specialCategoryTitle")
  }

  return headers
}

export const makeTasksPerClientBaseFilename = (clientCode) => {
  return `REP_GEN_${clientCode}_TareasPorCliente`
}

/**
 * @param {AnyTaskReportItem} record
 * @returns {record is IndividualTaskReportItem | GlobalTaskReportItem}
 */
export function isTaskReportItem(record) {
  return !("isPlaceholder" in record) || record.isPlaceholder === false
}

/**
 * @param {AnyTaskReportItem} record
 * @returns {record is IndividualTaskReportItem}
 */
export function isIndividualTaskReportItem(record) {
  return "isPlaceholder" in record && record.isPlaceholder === false
}
